import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/common/Primitives";
import { api, errorCode } from "@/lib/api";
import { errorText, useLang } from "@/lib/i18n";
import { NAV, Logo, LangSwitch, PHONE } from "./Header";

/* ------------------------------------------------------------------ newsletter */

const field = "h-12 w-full rounded-md border border-white/15 bg-white/[0.06] px-4 text-[0.9375rem] text-white outline-none transition-[border-color,background-color] placeholder:text-white/40 focus:border-white/50 focus:bg-white/[0.09]";

export const Newsletter = () => {
  const { lang, tx } = useLang();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    try {
      const { data } = await api.post("/newsletter", { email, name, lang });
      setState("done");
      toast.success(data.already ? tx("Du er alt påmeld nyheitsbrevet.", "You're already on the list.") : tx("Takk! Du er påmeld nyheitsbrevet.", "Thank you! You're on the list."));
    } catch (err) {
      setState("idle");
      toast.error(errorText(errorCode(err), lang));
    }
  };
  return (
    <div data-testid="newsletter-section">
      <p className="text-[1.5rem] font-medium leading-tight tracking-[-0.015em] text-white">{tx("Nytt frå Baronen og hotellet", "News from Baronen and the hotel")}</p>
      <p className="mt-3 max-w-sm text-[0.9375rem] leading-relaxed text-white/60">
        {tx("Få beskjed om menyar, smakingar og arrangement. Du kan melde deg av når du vil.", "Hear about menus, tastings and events. Unsubscribe whenever you like.")}
      </p>
      {state === "done" ? (
        <div className="mt-5 flex items-center gap-3 rounded-md bg-white/[0.08] px-5 py-3.5 text-[0.9375rem] text-white" data-testid="newsletter-success">
          <Check className="h-5 w-5" /> {tx("Takk! Du er påmeld nyheitsbrevet.", "Thank you! You're on the list.")}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 grid gap-3" data-testid="footer-newsletter-form">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={tx("Namn (valfritt)", "Name (optional)")} className={field} data-testid="newsletter-name-input" aria-label={tx("Namn", "Name")} autoComplete="name" />
          <div className="flex items-center gap-1 rounded-md border border-white/15 bg-white/[0.06] p-1 transition-[border-color] focus-within:border-white/50">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={tx("E-postadresse", "Email address")} className="h-10 min-w-0 flex-1 bg-transparent pl-3 text-[0.9375rem] text-white outline-none placeholder:text-white/40" data-testid="newsletter-email-input" aria-label={tx("E-postadresse", "Email address")} autoComplete="email" />
            <Button type="submit" variant="inverse" className="h-10 shrink-0 px-4" disabled={state === "loading"} data-testid="newsletter-submit">{state === "loading" ? tx("Sender …", "Sending …") : tx("Meld meg på", "Sign up")}</Button>
          </div>
          <p className="text-xs leading-relaxed text-white/45">
            {tx("Me brukar e-postadressa til å sende deg nyheitsbrevet.", "We use your email address to send you the newsletter.")}{" "}
            <Link to="/praktisk#vilkar" className="underline underline-offset-2 hover:text-white/80">{tx("Les personvernerklæringa", "Read the privacy policy")}</Link>
          </p>
        </form>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ footer */

const Col = ({ title, children }) => (
  <div>
    <p className="label-caps text-white/45">{title}</p>
    <ul className="mt-6 space-y-3 text-[0.9375rem] text-white/70">{children}</ul>
  </div>
);

const FLink = ({ to, children, testid }) => (
  <li><Link to={to} className="inline-flex items-center transition-colors hover:text-white" data-testid={testid}>{children}</Link></li>
);

export const Footer = ({ compact = false }) => {
  const { tx } = useLang();
  return (
    <footer className="grain relative overflow-hidden bg-fjord text-white" data-testid="site-footer">
      {!compact && (
        <Container className="pt-20 lg:pt-28">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-6">
              <Logo className="h-9" light />
              <p className="mt-10 max-w-md text-[1.5rem] font-medium leading-[1.25] tracking-[-0.015em] text-white sm:text-[1.75rem]" data-testid="footer-statement">
                {tx("Rosendal Fjordhotel ligg ved Hardangerfjorden, med Baroniet i gåavstand og fjellturar i nærleiken.", "Rosendal Fjordhotel sits on the Hardangerfjord, with the Barony within walking distance and mountain hikes close by.")}
              </p>
              <address className="mt-8 space-y-1 text-[0.9375rem] not-italic leading-relaxed text-white/70">
                <p className="text-white">Rosendal Fjordhotel</p>
                <p>Rosendalsvegen 46, 5470 Rosendal</p>
                <p><a href={PHONE.tel} className="transition-colors hover:text-white tabular" data-testid="footer-phone-link">{PHONE.intl}</a></p>
                <p><a href="mailto:booking@rosendal-fjordhotel.no" className="break-all transition-colors hover:text-white" data-testid="footer-email">booking@rosendal-fjordhotel.no</a></p>
                <p>
                  <a href="https://maps.google.com/?q=Rosendalsvegen+46,+5470+Rosendal" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 transition-colors hover:text-white" data-testid="footer-directions">
                    {tx("Opne vegskildring", "Open directions")} <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </p>
              </address>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 lg:pt-2">
              <Newsletter />
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 gap-x-8 gap-y-12 border-t border-white/10 pt-12 sm:grid-cols-3 lg:mt-20 lg:pt-14">
            <Col title={tx("Utforsk", "Explore")}>
              {NAV.map((n) => <FLink key={n.to} to={n.to} testid={`footer-nav-${n.id}`}>{tx(n.nn, n.en)}</FLink>)}
            </Col>
            <Col title={tx("Bestilling", "Booking")}>
              <FLink to="/bestill" testid="footer-booking-cta">{tx("Bestill rom", "Book a room")}</FLink>
              <FLink to="/mi-bestilling" testid="footer-my-booking">{tx("Mi bestilling", "My booking")}</FLink>
              <FLink to="/mat#bord">{tx("Spør om bord", "Ask about a table")}</FLink>
              <FLink to="/arrangement#forespurnad">{tx("Spør om selskap eller møte", "Ask about an event or meeting")}</FLink>
              <FLink to="/praktisk#vilkar">{tx("Bestillingsvilkår", "Booking terms")}</FLink>
            </Col>
            <Col title={tx("Godt å vite", "Good to know")}>
              <li className="tabular">{tx("Innsjekk frå kl. 16.00", "Check-in from 16:00")}</li>
              <li className="tabular">{tx("Utsjekk innan kl. 11.00", "Check-out by 11:00")}</li>
              <li className="tabular">{tx("Frukost måndag–fredag: 06.30–10.00", "Breakfast Mon–Fri: 06:30–10:00")}</li>
              <li className="tabular">{tx("Frukost laurdag–søndag: 07.00–10.00", "Breakfast Sat–Sun: 07:00–10:00")}</li>
              <li>{tx("Gratis parkering", "Free parking")}</li>
              <li>{tx("Elbillading tilgjengeleg", "EV charging available")}</li>
            </Col>
          </div>
        </Container>
      )}

      <Container className={`${compact ? "py-6" : "mt-14 border-t border-white/10 pt-6 lg:mt-16"}`}>
        <div className="flex flex-col gap-5 text-sm text-white/55 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            {compact && <Logo className="h-6" light />}
            <p className="tabular">© {new Date().getFullYear()} Rosendal Fjordhotel</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <Link to="/praktisk#vilkar" className="transition-colors hover:text-white">{tx("Personvern", "Privacy")}</Link>
            <Link to="/praktisk#vilkar" className="transition-colors hover:text-white">{tx("Bestillingsvilkår", "Booking terms")}</Link>
            <Link to="/admin/login" className="transition-colors hover:text-white" data-testid="footer-admin-link">{tx("For tilsette", "Staff login")}</Link>
            <LangSwitch testid="footer-language-toggle" light />
          </div>
        </div>
      </Container>

      {!compact && (
        <div aria-hidden className="pointer-events-none select-none">
          <Container>
            <p className="-mb-[0.2em] mt-10 overflow-hidden whitespace-nowrap text-[clamp(3.5rem,11.5vw,11rem)] font-medium leading-none tracking-[-0.02em] text-white/[0.04] lg:mt-12">
              Rosendal Fjordhotel
            </p>
          </Container>
        </div>
      )}
      {compact && <div className="h-20 lg:hidden" aria-hidden />}
    </footer>
  );
};
